import { css } from "@emotion/css";
import { FormControl, useTheme } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { Element } from "react-scroll";

import { dayjs } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";

import type { IconType } from "../../types/subText.js";
import { FormLabel } from "../FormLabel.js";
import { HelperText } from "../HelperText/HelperText.js";
import { ReadOnlyContent } from "../ReadOnlyContent/ReadOnlyContent.js";

const defaultProps: Partial<TimePickerProps> = {
  label: undefined,
  errorMessage: undefined,
  disabled: false,
  variant: "outlined",
  subtext: undefined,
  subtextIcon: undefined,
  readOnly: false,
  isMobile: false,
};

export interface TimePickerProps {
  name: string;
  label?: string;
  errorMessage?: string;
  value: string | Dayjs | null;
  onChange: (e: Dayjs | null) => void;
  variant?: "outlined" | "filled" | "standard";
  disabled?: boolean;
  subtext?: string;
  subtextIcon?: IconType;
  readOnly?: boolean;
  isMobile?: boolean;
}

export const TimePicker = (props: TimePickerProps) => {
  const allProps: TimePickerProps = { ...defaultProps, ...props };
  const {
    name,
    label,
    value,
    errorMessage,
    onChange,
    disabled,
    variant,
    subtext,
    subtextIcon,
    readOnly,
    isMobile,
  } = allProps;

  const theme = useTheme();

  const normaliseValue = (v: string | Dayjs | null): Dayjs | null => {
    if (!v) return null;
    if (typeof v === "string")
      return dayjs(v, "HH:mm", true).isValid()
        ? dayjs(v, "HH:mm", true)
        : dayjs(v);
    return v;
  };

  if (readOnly) {
    return (
      <ReadOnlyContent
        label={label}
        content={[dayjs(value).format("HH:mm")]}
        dense
        subtextIcon={subtextIcon!}
      />
    );
  }

  const iconColor = () => {
    if (disabled) return theme.palette.text.disabled;
    if (errorMessage) return theme.palette.error.main;
    return theme.palette.primary.main;
  };

  const pickerTextFieldSx = {
    "& .MuiPickersOutlinedInput-root": {
      backgroundColor: theme.palette.common.paleGray,
    },
    svg: { color: iconColor() },
  };

  return (
    <Element
      name={name}
      css={css`
        margin-bottom: ${theme.spacing(4)};
      `}
    >
      {label && <FormLabel label={label} error={!!errorMessage} />}
      <FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {isMobile ? (
            <div data-testid={`formField-${name}-time`}>
              <MobileTimePicker
                value={normaliseValue(value)}
                onChange={(v) => {
                  onChange(v);
                }}
                ampm={false}
                disabled={disabled}
                slotProps={{
                  textField: {
                    error: !!errorMessage,
                    variant,
                    sx: pickerTextFieldSx,
                    inputProps: {
                      "data-testid": `formField-${name}-time-input`,
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div data-testid={`formField-${name}-time`}>
              <DesktopTimePicker
                value={normaliseValue(value)}
                onChange={(v) => {
                  onChange(v);
                }}
                ampm={false}
                disabled={disabled}
                slotProps={{
                  inputAdornment: { position: "start" },
                  textField: {
                    error: !!errorMessage,
                    variant,
                    sx: pickerTextFieldSx,
                    fullWidth: true,
                    inputProps: {
                      "data-testid": `formField-${name}-time-input`,
                    },
                  },
                }}
              />
            </div>
          )}
        </LocalizationProvider>
      </FormControl>
      {(errorMessage || subtext) && (
        <HelperText subtext={subtext} errorMessage={errorMessage} />
      )}
    </Element>
  );
};
